import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { authApi } from "@/api/auth";
import { accountApi } from "@/api/account";
import { useSession } from "@/stores/session";
import { can } from "@/lib/authz";
import { ApiError } from "@/api/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/password-input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { formatRelative } from "@/lib/format";
import { Download, KeyRound } from "lucide-react";

export const Route = createFileRoute("/_authed/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const user = useSession((s) => s.user);
  const clearSession = useSession((s) => s.clear);
  const canDelete = can(user?.role, "delete_account");

  // Change-password modal.
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState(false);
  const chgPw = useMutation({
    mutationFn: () => authApi.changePassword(pwCur, pwNew),
    onSuccess: () => {
      setPwOk(true);
      setPwCur("");
      setPwNew("");
      setPwErr(null);
    },
    onError: (e) => {
      setPwOk(false);
      if (e instanceof ApiError && e.status === 401) setPwErr("Current password is incorrect");
      else setPwErr("Password change failed");
    },
  });
  function openPw() {
    setPwCur("");
    setPwNew("");
    setPwErr(null);
    setPwOk(false);
    setPwOpen(true);
  }

  // Data export (SaaS C.2).
  const exportsQ = useQuery({ queryKey: ["exports"], queryFn: () => accountApi.listExports() });
  const reqExport = useMutation({
    mutationFn: () => accountApi.requestExport(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["exports"] }),
  });

  // Account deletion (SaaS C.2).
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteAcct = useMutation({
    mutationFn: () => accountApi.deleteAccount(),
    onSuccess: () => {
      setConfirmDelete(false);
      clearSession();
      void nav({ to: "/login" });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Account" title="Profile" />

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b px-5 py-3">
          <div>
            <h2 className="font-display text-[15px] font-semibold tracking-tight">User</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">Your personal account details.</p>
          </div>
          <Button variant="outline" size="sm" onClick={openPw} className="gap-1.5">
            <KeyRound size={14} /> Change password
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-3">
          <Meta label="Email" value={user?.email ?? "—"} />
          <Meta label="User ID" value={user?.id ?? "—"} mono />
          <Meta label="Role" value={user?.role ?? "—"} cap />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Export your data</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Download a copy of your account's environments, apps, functions, runs and events. We
            prepare it in the background and email you a link when it's ready.
          </p>
        </div>
        <div className="p-5">
          <Button
            onClick={() => reqExport.mutate()}
            disabled={reqExport.isPending}
            className="gap-1.5 bg-brand text-primary-foreground hover:bg-brand/90"
          >
            <Download size={14} /> {reqExport.isPending ? "Requesting…" : "Request export"}
          </Button>
        </div>
        {(exportsQ.data?.data ?? []).length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Requested</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="pr-5"><span className="sr-only">Download</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(exportsQ.data?.data ?? []).map((x) => (
                <TableRow key={x.id}>
                  <TableCell className="tabular pl-5 font-mono text-[13px] text-muted-foreground">{formatRelative(x.created_at)}</TableCell>
                  <TableCell className="font-mono text-[13px]">{x.status}</TableCell>
                  <TableCell className="pr-5 text-right">
                    {x.status === "ready" ? (
                      <a href={accountApi.downloadUrl(x.id)}
                        className="text-[13px] font-medium text-brand hover:underline">
                        Download
                      </a>
                    ) : (
                      <span className="text-[13px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {canDelete && (
        <Card className="overflow-hidden border-fail/30">
          <div className="border-b border-fail/20 bg-fail/5 px-5 py-3">
            <h2 className="font-display text-[15px] font-semibold tracking-tight text-fail">Delete account</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Schedules your account for permanent deletion. You have a 30-day grace period to
              restore it from the emailed link before it's erased for good.
            </p>
          </div>
          <div className="p-5">
            <Button
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteAcct.isPending}
            >
              Delete account
            </Button>
          </div>
        </Card>
      )}

      {/* Change-password modal */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one (at least 8 characters).
            </DialogDescription>
          </DialogHeader>
          {pwOk ? (
            <div className="space-y-4">
              <p className="text-sm text-ok">Your password has been changed.</p>
              <DialogFooter>
                <Button onClick={() => setPwOpen(false)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); chgPw.mutate(); }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="pw-cur">Current password</Label>
                <PasswordInput id="pw-cur" required autoFocus value={pwCur} onChange={(e) => setPwCur(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-new">New password</Label>
                <PasswordInput id="pw-new" required minLength={8} value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
              </div>
              {pwErr && <p role="alert" className="text-sm text-fail">{pwErr}</p>}
              <DialogFooter className="gap-2 sm:gap-2">
                <Button type="button" variant="ghost" onClick={() => setPwOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={chgPw.isPending || !pwCur || !pwNew}
                  className="bg-brand text-primary-foreground hover:bg-brand/90">
                  {chgPw.isPending ? "Saving…" : "Change password"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this account?"
        description="Your account is scheduled for permanent deletion after a 30-day grace period. You'll be signed out immediately and everyone on the account loses access. This will:"
        affected={[
          "Log out every user on the account",
          "Cancel all running and queued functions",
          "Permanently erase all environments, apps, functions, runs and events after 30 days",
        ]}
        confirmLabel="Delete account"
        loading={deleteAcct.isPending}
        onConfirm={() => deleteAcct.mutate()}
      />
    </div>
  );
}

function Meta({ label, value, mono, cap }: { label: string; value: string; mono?: boolean; cap?: boolean }) {
  return (
    <div className="bg-card px-4 py-3">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "break-all font-mono text-[13px]" : cap ? "text-[13px] capitalize" : "text-[13px]"}>{value}</div>
    </div>
  );
}
